"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useListMembers } from "@/lib/api/generated";
import type { UserResponse } from "@/lib/api/model";
import type { ProfileNote } from "@/hooks/use-profile-notes";

const MENTION_TOKEN_RE = /<@([0-9a-f-]{36})>/gi;

interface ResolverCtx {
  members: Map<string, UserResponse>;
  isLoading: boolean;
}

const MentionResolverContext = createContext<ResolverCtx>({
  members: new Map(),
  isLoading: false,
});

type MentionableNote = {
  content: string;
  replies?: MentionableNote[];
};

function collectIds(notes: MentionableNote[], out: Set<string>) {
  for (const note of notes) {
    let m: RegExpExecArray | null;
    MENTION_TOKEN_RE.lastIndex = 0;
    while ((m = MENTION_TOKEN_RE.exec(note.content))) {
      out.add(m[1].toLowerCase());
    }
    if (note.replies?.length) collectIds(note.replies, out);
  }
}

export function MentionResolverProvider({
  notes,
  children,
}: {
  notes: MentionableNote[] | ProfileNote[];
  children: ReactNode;
}) {
  const ids = useMemo(() => {
    const set = new Set<string>();
    collectIds(notes, set);
    return Array.from(set).sort();
  }, [notes]);

  const { data, isFetching } = useListMembers(
    {
      ids: ids.length > 0 ? ids : undefined,
      per_page: 100,
      include_suspended: true,
    },
    {
      query: {
        enabled: ids.length > 0,
        staleTime: 60_000,
      },
    },
  );

  const members = useMemo(() => {
    const map = new Map<string, UserResponse>();
    for (const m of data?.members ?? []) {
      map.set(m.id.toLowerCase(), m);
    }
    return map;
  }, [data]);

  const isLoading = ids.length > 0 && !data && isFetching;

  const value = useMemo(
    () => ({ members, isLoading }),
    [members, isLoading],
  );

  return (
    <MentionResolverContext.Provider value={value}>
      {children}
    </MentionResolverContext.Provider>
  );
}

export function useMentionResolver(): ResolverCtx {
  return useContext(MentionResolverContext);
}

export function useMentionLabelLookup(): (id: string) => string | null {
  const { members } = useMentionResolver();
  return (id: string) => {
    const m = members.get(id.toLowerCase());
    if (!m) return null;
    const name = `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim();
    return name || m.email;
  };
}
